import { useCallback, useMemo, useState } from 'react';

import type { TabProps } from './Tab';

export type UseTabs<T extends string> = {
  /**
   * The Tabs data
   */
  tabs: Array<Pick<TabProps, 'isActive' | 'onSelect'> & { label: T }>;
  /**
   * The active tab
   */
  activeTab: T;
  /**
   * Resets the active tab to the default or first tab.
   * Advanced usage, ideally the tabs should manage themselves.
   */
  resetTab: () => void;
};

/**
 * You provide a list of tab IDs, this hook will give you back an
 * array which contains data for the tabs, managing which tab is active.
 *
 * Does not include an icon or badge count, as those are optional,
 * however you could can manually inject them or create a new array based on this.
 *
 * @example
 * const MyTabs = () => {
 *  const tabs = useTabs({ tabIds: ['Foo', 'Bar', 'Baz'] });
 *  return (
 *   <Tabs>
 *     {tabs.tabs.map((tab) => (
 *       <Tabs.Tab
 *         key={tab.label}
 *         {...tab}
 *       />
 *     ))}
 *   </Tabs>
 * };
 */
const useTabs = <T extends string>({
  tabIds,
  defaultTab,
}: {
  tabIds: readonly T[];
  defaultTab?: T;
}): UseTabs<T> => {
  const [activeTab, setActiveTab] = useState<T>(defaultTab || tabIds[0] as T);
  const tabs = useMemo(() => {
    return tabIds.map((tabId) => ({
      label: tabId,
      isActive: tabId === activeTab,
      onSelect: () => setActiveTab(tabId),
    }));
  }, [tabIds, activeTab, setActiveTab]);
  return {
    tabs,
    activeTab,
    //  This is for advanced usage, ideally the tabs should manage themselves
    resetTab: useCallback(() => setActiveTab(defaultTab || tabIds[0] as T), [defaultTab, tabIds]), // Reset to the first tab
  };
};

export default useTabs;
